/**
 * THE single source of truth for the SELECTABLE UNIVERSES — the ordered list of
 * universes the scanner / breadth dropdowns offer and their API routes accept.
 *
 * Before this file the same list was hardcoded in ~13 places (3 route
 * KNOWN_UNIVERSES, 3 builder UNIVERSE_LABELS, PRECOMPUTE_UNIVERSES, and the
 * frontend dropdowns) with no reconciliation, so adding a universe (e.g. `base`)
 * silently failed wherever a copy was missed — a route/builder would default the
 * unknown slug to `sp_1500` or `all`, rendering the wrong list.
 *
 * Now: add a universe HERE once. If it's a new index-membership list, also add
 * its slug + label in `slugs.ts` (its label is read from `INDEX_LABEL`).
 * Everything downstream — dropdowns, route whitelists, builder label-maps, the
 * breadth-worker's precompute set — derives from `SELECTABLE_UNIVERSE_SLUGS`.
 *
 * NOT covered (deliberately separate systems): the Bucket-charts pane set
 * (`BUCKET_UNIVERSES`, a curated subset that generates one pane each), Live
 * Breadth's breadth-code list (`BREADTH_UNIVERSES` — NY/NQ/… codes), and the
 * Data-Maintenance repair picker. Those use `universeLabel()` for labels but
 * keep their own slug sets.
 */
import { INDEX_LABEL, type IndexSlug } from "./slugs";
import { GENERATED_LISTS, generatedListOf } from "./generated";

/** Universes that are NOT index-membership lists — the whole active panel.
 *  `all` = A/D-type-filtered stocks; `everything` = every instrument type. */
export const SPECIAL_UNIVERSE_LABEL: Record<string, string> = {
  all: "All US Stocks",
  everything: "Everything (all types)",
};

/** The ordered slugs offered in the scanner / breadth universe dropdowns.
 *  A curated subset of index lists (note: NOT sp400/sp600) + the specials. */
export const SELECTABLE_UNIVERSE_SLUGS = [
  "sp_1500",
  "sp500",
  "nyse",
  "nasdaq",
  "dow_jones",
  "nasdaq_100",
  "russell_1000",
  "russell_2000",
  "base",
  "all",
  "everything",
] as const;

export type UniverseSlug = (typeof SELECTABLE_UNIVERSE_SLUGS)[number];

/** Human label for ANY universe slug — a special, an index list, or (fallback)
 *  the slug itself. Use this everywhere a universe needs a display name. */
export function universeLabel(slug: string): string {
  return SPECIAL_UNIVERSE_LABEL[slug] ?? INDEX_LABEL[slug as IndexSlug] ?? generatedListOf(slug)?.label ?? slug;
}

/** `{ slug, label }` pairs in display order — bind dropdowns to this. */
export const SELECTABLE_UNIVERSES: ReadonlyArray<{ slug: string; label: string }> =
  SELECTABLE_UNIVERSE_SLUGS.map((slug) => ({ slug, label: universeLabel(slug) }));

const KNOWN = new Set<string>(SELECTABLE_UNIVERSE_SLUGS);

/** Is this a universe the scanner/breadth routes + builders accept? Whitelists
 *  used to default-away unknown slugs; they now all funnel through this. */
export function isSelectableUniverse(slug: string): boolean {
  return KNOWN.has(slug);
}

/**
 * Lists the Stock Screener offers beside the selectable universes, and nothing
 * else does. Its query reads a list's members in the request
 * (`readIndexSymbols`, lib/scanner/run.ts), so these cost the breadth worker
 * nothing; each one made selectable would be another dashboard, bucket and
 * Top-Down build in every pass.
 */
export const SCREENER_LIST_SLUGS = ["amex", "mega_cap", "large_cap", "mid_cap", "small_cap", "micro_cap", "etfs"] as const;

/** What the Stock Screener's Universe menu offers. */
export const SCREENER_UNIVERSES: ReadonlyArray<{ slug: string; label: string }> = [
  ...SELECTABLE_UNIVERSES,
  ...SCREENER_LIST_SLUGS.map((slug) => ({ slug, label: universeLabel(slug) })),
];

/** The universes the breadth-worker precomputes each cycle (= every selectable
 *  one). Derived so a new universe is precomputed automatically. */
export const PRECOMPUTE_UNIVERSE_SLUGS: readonly string[] = SELECTABLE_UNIVERSE_SLUGS;

/**
 * The Scanner's universes (its sectors, industries and stocks, the Top-Down
 * snapshot): every selectable one and the exchanges' all-issues lists
 * (lib/indices/generated.ts; README "The Scanner reads the one list"). An
 * all-issues list is read on the issues the scanner prices, its stocks and
 * ETFs: the panel holds no preferred, closed-end fund, warrant, unit or right,
 * so the Scanner says how many of the list's issues it leaves out
 * (`TopDownPayload.listCount`), never a smaller list under the same name.
 * The breadth worker builds only the Scanner's snapshot for those three: a
 * breadth reading of an all-issues list is its tape (`breadth_daily`), not the
 * scanner's rows.
 */
export const SCANNER_ISSUE_LIST_SLUGS: readonly string[] = GENERATED_LISTS.map((g) => g.slug);
export const SCANNER_UNIVERSE_SLUGS: readonly string[] = [...SELECTABLE_UNIVERSE_SLUGS, ...SCANNER_ISSUE_LIST_SLUGS];

const SCANNER_KNOWN = new Set<string>(SCANNER_UNIVERSE_SLUGS);

/** A universe the Scanner's routes and its Top-Down build accept. */
export function isScannerUniverse(slug: string): boolean {
  return SCANNER_KNOWN.has(slug);
}
